import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  Database, 
  Save, 
  RefreshCw, 
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle,
  FileCode,
  Settings,
  History,
  Shield,
  Table,
  Columns,
  Clock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const SchemaManagement = () => {
  const { user } = useAuth();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTable, setSelectedTable] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [editingColumn, setEditingColumn] = useState(null);
  const [backups, setBackups] = useState([]);
  const [validation, setValidation] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [tableSortBy, setTableSortBy] = useState('name'); // 'name' or 'columns'
  const [tableSortOrder, setTableSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Helper function to get sorted tables
  const getSortedTables = () => {
    if (!schema?.tables) return [];

    const tableEntries = Object.entries(schema.tables);

    return tableEntries.sort((a, b) => {
      const [nameA, dataA] = a;
      const [nameB, dataB] = b;

      let comparison = 0;

      if (tableSortBy === 'name') {
        comparison = nameA.localeCompare(nameB);
      } else if (tableSortBy === 'columns') {
        const columnsA = Object.keys(dataA.columns || {}).length;
        const columnsB = Object.keys(dataB.columns || {}).length;
        comparison = columnsA - columnsB;
      }

      return tableSortOrder === 'asc' ? comparison : -comparison;
    });
  };

  // Load schema data
  useEffect(() => {
    loadSchema();
    loadBackups();
    validateSchema();
  }, []);

  // Auto-select first table when switching to tables tab
  useEffect(() => {
    if (activeTab === 'tables' && schema?.tables && !selectedTable) {
      const sortedTables = getSortedTables();
      if (sortedTables.length > 0) {
        setSelectedTable(sortedTables[0][0]);
      }
    }
  }, [activeTab, schema, selectedTable, tableSortBy, tableSortOrder]);

  const loadSchema = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/schema', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load schema');
      }

      const data = await response.json();
      // Backend returns { success, data: { schema: { ... } } } based on schema_configuration.json
      // Support both shapes: {schema: {...}} or direct schema object
      const loadedSchema = data?.data?.schema || data?.data || null;
      setSchema(loadedSchema);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async () => {
    try {
      const response = await fetch('/api/schema/backups', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBackups(data.data);
      }
    } catch (err) {
      console.error('Failed to load backups:', err);
    }
  };

  const validateSchema = async () => {
    try {
      const response = await fetch('/api/schema/validate', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setValidation(data.data);
      }
    } catch (err) {
      console.error('Failed to validate schema:', err);
    }
  };

  const saveSchema = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await fetch('/api/schema', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ schema })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save schema');
      }

      const data = await response.json();
      setSuccess('Schema updated successfully!');
      setEditingTable(null);
      setEditingColumn(null);
      
      // Reload validation
      validateSchema();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const restoreFromBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to restore from backup: ${filename}? This will overwrite the current schema.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/schema/restore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ filename })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to restore schema');
      }

      setSuccess('Schema restored successfully!');
      loadSchema();
      loadBackups();
      validateSchema();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addTable = () => {
    const tableName = prompt('Enter table name:');
    if (!tableName) return;

    if (schema.tables[tableName]) {
      alert('Table already exists!');
      return;
    }

    const newTable = {
      description: '',
      columns: {
        [`${tableName}ID`]: 'INT IDENTITY(1,1) PRIMARY KEY'
      }
    };

    setSchema({
      ...schema,
      tables: {
        ...schema.tables,
        [tableName]: newTable
      }
    });
    
    // Auto-select the newly created table
    setSelectedTable(tableName);
  };

  const deleteTable = (tableName) => {
    if (!window.confirm(`Are you sure you want to delete table "${tableName}"?`)) {
      return;
    }

    const newTables = { ...schema.tables };
    delete newTables[tableName];
    setSchema({
      ...schema,
      tables: newTables
    });
    
    // Clear selection if the deleted table was selected
    if (selectedTable === tableName) {
      setSelectedTable(null);
    }
  };

  const addColumn = (tableName) => {
    const columnName = prompt('Enter column name:');
    if (!columnName) return;

    const columnType = prompt('Enter column type (e.g., NVARCHAR(100), INT, DATETIME2):');
    if (!columnType) return;

    if (schema.tables[tableName].columns[columnName]) {
      alert('Column already exists!');
      return;
    }

    setSchema({
      ...schema,
      tables: {
        ...schema.tables,
        [tableName]: {
          ...schema.tables[tableName],
          columns: {
            ...schema.tables[tableName].columns,
            [columnName]: columnType
          }
        }
      }
    });
  };

  const deleteColumn = (tableName, columnName) => {
    if (!window.confirm(`Are you sure you want to delete column "${columnName}" from table "${tableName}"?`)) {
      return;
    }

    const newColumns = { ...schema.tables[tableName].columns };
    delete newColumns[columnName];

    setSchema({
      ...schema,
      tables: {
        ...schema.tables,
        [tableName]: {
          ...schema.tables[tableName],
          columns: newColumns
        }
      }
    });
  };

  const updateColumn = (tableName, columnName, newType) => {
    setSchema({
      ...schema,
      tables: {
        ...schema.tables,
        [tableName]: {
          ...schema.tables[tableName],
          columns: {
            ...schema.tables[tableName].columns,
            [columnName]: newType
          }
        }
      }
    });
  };

  const updateTableDescription = (tableName, description) => {
    setSchema({
      ...schema,
      tables: {
        ...schema.tables,
        [tableName]: {
          ...schema.tables[tableName],
          description: description
        }
      }
    });
  };

  const syncWithEntityConfig = async () => {
    if (!window.confirm('This will sync all entity configurations with the schema and create missing tables/columns in all tenant databases. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      setError('');
      setSyncResult(null);

      // Step 1: Sync tenant overrides to entity configs
      console.log('Step 1: Syncing tenant overrides to entity configs...');
      const tenantSyncResponse = await fetch('/api/tenant-entity-overrides/sync-all/fields', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          entityName: 'customer',
          syncToDatabase: false  // Don't create DB columns yet
        })
      });

      let tenantSyncResult = null;
      if (tenantSyncResponse.ok) {
        const tenantData = await tenantSyncResponse.json();
        tenantSyncResult = tenantData.result;
        console.log('Tenant sync completed:', tenantSyncResult);
      }

      // Step 2: Sync schema columns to entity configs (NEW - reverse sync)
      console.log('Step 2: Syncing schema columns to entity configurations...');
      const schemaToEntityResponse = await fetch('/api/entity-config/sync-all/schema', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          syncFromSchema: true,  // Enable reverse sync: schema -> entity config
          syncDatabase: false    // Don't create DB columns yet
        })
      });

      let schemaToEntityResult = null;
      if (schemaToEntityResponse.ok) {
        const schemaToEntityData = await schemaToEntityResponse.json();
        schemaToEntityResult = schemaToEntityData.schemaToEntity;
        console.log('Schema-to-Entity sync completed:', schemaToEntityResult);
      }

      // Step 3: Sync entity configs to schema configuration (forward sync)
      console.log('Step 3: Syncing entity configs to schema configuration...');
      const entitySyncResponse = await fetch('/api/entity-config/sync-all/schema', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          syncFromSchema: false, // Already done, skip reverse sync
          syncDatabase: true     // Create DB columns
        })
      });

      if (!entitySyncResponse.ok) {
        const errorData = await entitySyncResponse.json();
        throw new Error(errorData.message || 'Failed to sync entity configs to schema');
      }

      const entityData = await entitySyncResponse.json();
      console.log('Entity-Schema sync completed:', entityData);
      
      // Combine results
      const combinedResult = {
        tenantSync: tenantSyncResult,
        schemaToEntity: schemaToEntityResult,
        entitySync: entityData,
        summary: {
          totalTenants: tenantSyncResult?.summary?.totalTenants || 0,
          totalFieldsAdded: tenantSyncResult?.summary?.totalFieldsAdded || 0,
          newFields: tenantSyncResult?.summary?.newFields || [],
          fieldsAddedFromSchema: schemaToEntityResult?.summary?.totalFieldsAdded || 0,
          entitiesSynced: entityData?.summary?.total || 0
        },
        schemaSync: entityData?.results?.[0] || null
      };
      
      setSyncResult(combinedResult);
      
      // Reload schema to show updates
      await loadSchema();
      await validateSchema();
      
      const tenantFields = combinedResult.summary.totalFieldsAdded;
      const schemaFields = combinedResult.summary.fieldsAddedFromSchema;
      const totalEntities = combinedResult.summary.entitiesSynced;
      
      let successMsg = 'Sync completed! ';
      const parts = [];
      if (tenantFields > 0) parts.push(`${tenantFields} field(s) from tenants`);
      if (schemaFields > 0) parts.push(`${schemaFields} field(s) from schema`);
      if (totalEntities > 0) parts.push(`${totalEntities} entity config(s) synchronized`);
      
      setSuccess(`Sync completed! ${parts.join(' + ')}`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);

    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const toggleTableSort = (sortType) => {
    if (tableSortBy === sortType) {
      // Toggle order
      setTableSortOrder(tableSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Change sort type
      setTableSortBy(sortType);
      setTableSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading schema configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6 max-w-md">
          <div className="flex items-center space-x-3 mb-4">
            <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            <h2 className="text-lg font-semibold text-red-800 dark:text-red-200">Error Loading Schema</h2>
          </div>
          <p className="text-red-700 dark:text-red-300 mb-4">{error}</p>
          <button 
            onClick={loadSchema}
            className="bg-red-600 dark:bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
          <Database className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Schema Data</h2>
          <p className="text-gray-600 dark:text-gray-300">Unable to load schema configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Database className="h-7 w-7" />
            <span>Schema Management</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage database schema configuration and structure
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={syncWithEntityConfig}
            disabled={syncing}
            className="bg-purple-600 dark:bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            title="Sync with Entity Configurations"
          >
            {syncing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Syncing...</span>
              </>
            ) : (
              <>
                <Database className="h-4 w-4" />
                <span>Sync with Entities</span>
              </>
            )}
          </button>
          <button
            onClick={loadSchema}
            className="bg-gray-600 dark:bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button 
            onClick={saveSchema} 
            disabled={saving}
            className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Schema</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 flex items-start space-x-3">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Success</h3>
            <p className="text-sm text-green-700 dark:text-green-300 mt-1">{success}</p>
          </div>
        </div>
      )}

      {/* Sync Results */}
      {syncResult && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Database className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Entity Sync Results</h3>
            </div>
            <button
              onClick={() => setSyncResult(null)}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
          
          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Tenants Processed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{syncResult.summary?.totalTenants || 0}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Tenant Fields Added</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{syncResult.summary?.totalFieldsAdded || 0}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Schema Fields Added</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{syncResult.summary?.fieldsAddedFromSchema || 0}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Entities Synced</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{syncResult.summary?.entitiesSynced || 0}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Status</p>
              <div className="flex items-center space-x-2 mt-1">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-semibold text-green-600 dark:text-green-400">Complete</span>
              </div>
            </div>
          </div>

          {/* New Fields from Tenant Overrides */}
          {syncResult.summary?.newFields && syncResult.summary.newFields.length > 0 && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">New Fields from Tenant Overrides</h4>
              <div className="flex flex-wrap gap-2">
                {syncResult.summary.newFields.map((field, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                    {field}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* New Fields from Schema */}
          {syncResult.schemaToEntity?.results && syncResult.schemaToEntity.results.some(r => r.fieldsAdded && r.fieldsAdded.length > 0) && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-4 border border-orange-200 dark:border-orange-700">
              <h4 className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-3">New Fields Added from Schema</h4>
              {syncResult.schemaToEntity.results
                .filter(r => r.fieldsAdded && r.fieldsAdded.length > 0)
                .map((result, index) => (
                  <div key={index} className="mb-3 last:mb-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <Database className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{result.entity}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({result.fieldsAdded.length} field(s))</span>
                    </div>
                    <div className="flex flex-wrap gap-2 ml-6">
                      {result.fieldsAdded.map((field, fieldIndex) => (
                        <span key={fieldIndex} className="px-3 py-1 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded-full text-xs font-medium">
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Schema Changes by Table */}
          {syncResult.entitySync?.results && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">Schema Changes by Table</h4>
              {syncResult.entitySync.results.map((result, index) => (
                <div key={index} className="mb-4">
                  {result.syncResult?.changes?.tables && Object.keys(result.syncResult.changes.tables).length > 0 && (
                    <div className="space-y-2">
                      {Object.entries(result.syncResult.changes.tables).map(([tableName, changes]) => (
                        <div key={tableName} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Table className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{tableName}</span>
                            </div>
                            <div className="flex items-center space-x-3 text-xs">
                              {changes.added.length > 0 && (
                                <span className="text-green-600 dark:text-green-400">
                                  +{changes.added.length} added
                                </span>
                              )}
                              {changes.updated.length > 0 && (
                                <span className="text-blue-600 dark:text-blue-400">
                                  ~{changes.updated.length} updated
                                </span>
                              )}
                            </div>
                          </div>
                          {(changes.added.length > 0 || changes.updated.length > 0) && (
                            <div className="mt-2 space-y-1">
                              {changes.added.length > 0 && (
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  <span className="font-medium">Added:</span> {changes.added.join(', ')}
                                </div>
                              )}
                              {changes.updated.length > 0 && (
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  <span className="font-medium">Updated:</span> {changes.updated.join(', ')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Database Updates */}
          {syncResult.entitySync?.results?.[0]?.databaseUpdates && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Database Updates</h4>
              {syncResult.entitySync.results[0].databaseUpdates.map((update, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Tenant: {update.tenant}</span>
                      {update.success ? (
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {update.addedColumns?.length || 0} column(s) added
                    </span>
                  </div>
                  {update.addedColumns && update.addedColumns.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Columns:</span> {update.addedColumns.join(', ')}
                    </div>
                  )}
                  {update.errors && update.errors.length > 0 && (
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                      <span className="font-medium">Errors:</span> {update.errors.map(e => e.column || e.error).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Tables</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {Object.keys(schema.tables || {}).length}
              </p>
            </div>
            <Table className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Columns</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {Object.values(schema.tables || {}).reduce((total, table) => 
                  total + Object.keys(table.columns || {}).length, 0
                )}
              </p>
            </div>
            <Columns className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Version</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {schema.metadata?.version || 'N/A'}
              </p>
            </div>
            <FileCode className="h-8 w-8 text-purple-600 dark:text-purple-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Database Type</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                {schema.metadata?.databaseType || 'N/A'}
              </p>
            </div>
            <Settings className="h-8 w-8 text-orange-600 dark:text-orange-400" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-1 p-2">
            <button 
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'overview' 
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              <FileCode className="h-4 w-4" />
              <span>Overview</span>
            </button>
            <button 
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'tables' 
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('tables')}
            >
              <Table className="h-4 w-4" />
              <span>Tables</span>
            </button>
            <button 
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'validation' 
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('validation')}
            >
              <Shield className="h-4 w-4" />
              <span>Validation</span>
            </button>
            <button 
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'backups' 
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('backups')}
            >
              <History className="h-4 w-4" />
              <span>Backups</span>
            </button>
          </div>
        </div>

        <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Schema Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Name</label>
                  <p className="text-base text-gray-900 dark:text-white mt-1">{schema.metadata?.name || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Version</label>
                  <p className="text-base text-gray-900 dark:text-white mt-1">{schema.metadata?.version || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Database Type</label>
                  <p className="text-base text-gray-900 dark:text-white mt-1">{schema.metadata?.databaseType || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>Last Updated</span>
                  </label>
                  <p className="text-base text-gray-900 dark:text-white mt-1">
                    {schema.metadata?.lastUpdated ? new Date(schema.metadata.lastUpdated).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tables</label>
                  <p className="text-base text-gray-900 dark:text-white mt-1">{Object.keys(schema.tables || {}).length}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Columns</label>
                  <p className="text-base text-gray-900 dark:text-white mt-1">
                    {Object.values(schema.tables || {}).reduce((total, table) => 
                      total + Object.keys(table.columns || {}).length, 0
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Description</h3>
              <p className="text-gray-700 dark:text-gray-300">
                {schema.metadata?.description || 'No description available.'}
              </p>
            </div>
          </div>
        )}

        {activeTab === 'tables' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Database Tables</h3>
              <button 
                onClick={addTable} 
                className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Table</span>
              </button>
            </div>

            {/* Split Panel Layout */}
            <div className="grid grid-cols-12 gap-6 min-h-[500px]">
              {/* Left Panel - Tables List */}
              <div className="col-span-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                      <Table className="h-4 w-4" />
                      <span>Tables ({Object.keys(schema.tables || {}).length})</span>
                    </h4>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleTableSort('name')}
                      className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors ${
                        tableSortBy === 'name'
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                      title="Sort by name"
                    >
                      <span>Name</span>
                      {tableSortBy === 'name' && (
                        tableSortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                      {tableSortBy !== 'name' && <ArrowUpDown className="h-3 w-3 opacity-50" />}
                    </button>
                    <button
                      onClick={() => toggleTableSort('columns')}
                      className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors ${
                        tableSortBy === 'columns'
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                      title="Sort by column count"
                    >
                      <span>Columns</span>
                      {tableSortBy === 'columns' && (
                        tableSortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      )}
                      {tableSortBy !== 'columns' && <ArrowUpDown className="h-3 w-3 opacity-50" />}
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[600px]">
                  {getSortedTables().map(([tableName, tableData]) => (
                    <div
                      key={tableName}
                      onClick={() => setSelectedTable(tableName)}
                      className={`px-4 py-3 cursor-pointer border-b border-gray-200 dark:border-gray-700 transition-colors ${
                        selectedTable === tableName
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-600 dark:border-l-blue-400'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium truncate ${
                            selectedTable === tableName
                              ? 'text-blue-900 dark:text-blue-100'
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            {tableName}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
                              {Object.keys(tableData.columns).length} columns
                            </span>
                          </div>
                        </div>
                        <Table className={`h-4 w-4 flex-shrink-0 ml-2 ${
                          selectedTable === tableName
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-400 dark:text-gray-500'
                        }`} />
                      </div>
                    </div>
                  ))}
                  {Object.keys(schema.tables || {}).length === 0 && (
                    <div className="text-center py-12">
                      <Table className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 dark:text-gray-300">No tables available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel - Table Details and Columns */}
              <div className="col-span-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {selectedTable ? (
                  <div className="flex flex-col h-full">
                    {/* Table Header */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Table className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedTable}</h4>
                          <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 rounded text-blue-700 dark:text-blue-300">
                            {Object.keys(schema.tables[selectedTable].columns).length} columns
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => setEditingTable(editingTable === selectedTable ? null : selectedTable)}
                            className={`px-3 py-1.5 rounded-lg flex items-center space-x-1 transition-colors ${
                              editingTable === selectedTable 
                                ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300' 
                                : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40'
                            }`}
                          >
                            <Edit className="h-4 w-4" />
                            <span>{editingTable === selectedTable ? 'Cancel' : 'Edit'}</span>
                          </button>
                          <button 
                            onClick={() => deleteTable(selectedTable)}
                            className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 flex items-center space-x-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Table Details */}
                    <div className="overflow-y-auto flex-1">
                      <div className="px-6 py-4 space-y-4">
                        {/* Description Section */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Description
                          </label>
                          {editingTable === selectedTable ? (
                            <textarea
                              value={schema.tables[selectedTable].description || ''}
                              onChange={(e) => updateTableDescription(selectedTable, e.target.value)}
                              placeholder="Enter table description..."
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              rows="3"
                            />
                          ) : (
                            <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                              {schema.tables[selectedTable].description || 'No description'}
                            </div>
                          )}
                        </div>

                        {/* Columns Section */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center space-x-2">
                              <Columns className="h-4 w-4" />
                              <span>Columns</span>
                            </h5>
                            <button 
                              onClick={() => addColumn(selectedTable)}
                              className="text-sm px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 flex items-center space-x-1"
                            >
                              <Plus className="h-3 w-3" />
                              <span>Add Column</span>
                            </button>
                          </div>

                          <div className="space-y-2">
                            {Object.entries(schema.tables[selectedTable].columns).map(([columnName, columnType]) => (
                              <div key={columnName} className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                                {editingColumn === `${selectedTable}.${columnName}` ? (
                                  <div className="flex items-center space-x-2">
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">{columnName}</div>
                                      <input
                                        type="text"
                                        value={columnType}
                                        onChange={(e) => updateColumn(selectedTable, columnName, e.target.value)}
                                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                      />
                                    </div>
                                    <button 
                                      onClick={() => setEditingColumn(null)}
                                      className="px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-1"
                                    >
                                      <Save className="h-3 w-3" />
                                      <span>Save</span>
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white">{columnName}</div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">{columnType}</div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <button 
                                        onClick={() => setEditingColumn(`${selectedTable}.${columnName}`)}
                                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                        title="Edit"
                                      >
                                        <Edit className="h-3 w-3" />
                                      </button>
                                      <button 
                                        onClick={() => deleteColumn(selectedTable, columnName)}
                                        className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                        title="Delete"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full py-12">
                    <div className="text-center">
                      <Table className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-300 font-medium">Select a table to view details</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Click on a table from the list to see its columns and properties
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'validation' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Schema Validation</span>
            </h3>
            {validation ? (
              <div className="space-y-6">
                <div className={`rounded-lg p-6 border-2 ${
                  validation.isValid 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' 
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                }`}>
                  <div className="flex items-center space-x-3">
                    {validation.isValid ? (
                      <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                    ) : (
                      <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                    )}
                    <div>
                      <h4 className={`text-xl font-bold ${
                        validation.isValid 
                          ? 'text-green-800 dark:text-green-200' 
                          : 'text-red-800 dark:text-red-200'
                      }`}>
                        Status: {validation.isValid ? 'Valid' : 'Invalid'}
                      </h4>
                      <p className={`text-sm ${
                        validation.isValid 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {validation.isValid ? 'Schema passes all validation checks' : 'Schema has validation issues that need attention'}
                      </p>
                    </div>
                  </div>
                </div>

                {validation.errors?.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-4 flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5" />
                      <span>Errors ({validation.errors.length})</span>
                    </h4>
                    <ul className="space-y-2">
                      {(validation.errors || []).map((error, index) => (
                        <li key={index} className="flex items-start space-x-2 text-sm text-red-700 dark:text-red-300">
                          <span className="text-red-500 dark:text-red-400 mt-0.5">•</span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {validation.warnings?.length > 0 && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-4 flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5" />
                      <span>Warnings ({validation.warnings.length})</span>
                    </h4>
                    <ul className="space-y-2">
                      {(validation.warnings || []).map((warning, index) => (
                        <li key={index} className="flex items-start space-x-2 text-sm text-yellow-700 dark:text-yellow-300">
                          <span className="text-yellow-500 dark:text-yellow-400 mt-0.5">•</span>
                          <span>{warning}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <label className="text-sm text-gray-600 dark:text-gray-400">Tables</label>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {validation.summary?.tableCount ?? Object.keys(schema.tables || {}).length}
                      </p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <label className="text-sm text-gray-600 dark:text-gray-400">Total Columns</label>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {validation.summary?.totalColumns ?? Object.values(schema.tables || {}).reduce((total, table) => total + Object.keys(table.columns || {}).length, 0)}
                      </p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <label className="text-sm text-gray-600 dark:text-gray-400">Has Relationships</label>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {validation.summary?.hasRelationships ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <label className="text-sm text-gray-600 dark:text-gray-400">Has Indexes</label>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {validation.summary?.hasIndexes ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Loading validation results...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'backups' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              <History className="h-5 w-5" />
              <span>Schema Backups</span>
            </h3>
            {backups.length > 0 ? (
              <div className="space-y-3">
                {backups.map((backup, index) => (
                  <div 
                    key={index} 
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center justify-between hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
                        <FileCode className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {backup.filename}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-1 mt-1">
                          <Clock className="h-3 w-3" />
                          <span>
                            {backup.date ? new Date(backup.date).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={() => restoreFromBackup(backup.filename)}
                      className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 flex items-center space-x-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>Restore</span>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <History className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300 font-medium">No backups available</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Backups will appear here when schema changes are saved
                </p>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default SchemaManagement;
